import React, { useContext, useEffect, useState } from "react";
import useTitle from "../Hooks/useTitle";
import { ComboBoxComponent } from "@syncfusion/ej2-react-dropdowns";
import { ButtonComponent } from "@syncfusion/ej2-react-buttons";
import axiosClient from "../Utils/axiosClient";
import ToastContext from "../Context/toastContext";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";
import { TextAreaComponent } from "@syncfusion/ej2-react-inputs";
import ContactModal from "../Components/ContactModal";
import { useLocation } from "react-router-dom";

const SendMessagePage = () => {
  const location = useLocation();
  const [data, setData] = useState([]);
  const [selectedNumber, setSelectedNumber] = useState(null);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [message, setMessage] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { t } = useTranslation();
  const { showToast } = useContext(ToastContext);

  useTitle(`${t("Send Message")} | Netileti`);

  useEffect(() => {
    if (location.state?.contact) {
      setSelectedContacts([{
        phoneNumber: location.state.contact.phoneNumber,
        definition: location.state.contact.definition
      }]);
    }
  }, [location.state]);

  const getData = async () => {
    try {
      const response = await axiosClient.get("/accounts");

      if (response.status === 200) {
        const formattedData = response.data.result.map((item) => ({
          phoneNumber: `${item.firmName} (${item.phoneNumber})`,
          phoneNumberId: item.phoneNumberId,
        }));

        setData(formattedData);
      } else {
        showToast(
          `${t("Error")}: ${response.statusText}`,
          "Http status :",
          response.status
        );
      }
    } catch (error) {
      showToast(
        `${t("Failed to fetch data. Please try again.")},${error.message}`,
        `${t("Error")}`
      );
    }
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    if (!selectedNumber) {
      showToast(`${t("Please select a phone number")}.`, `${t("Error")}`);
      return;
    }
    if (!selectedContacts) {
      showToast(`${t("Please select receipent")}.`, `${t("Error")}`);
      return;
    }
    const envelopeId = uuidv4();
    const errorMessages = [];

    const promises = selectedContacts.map(async (contact) => {
      const payload = {
        to: contact.phoneNumber,
        body: message,
        envelopeId,
      };
      try {
        const response = await axiosClient.post(
          `/sendmessage/${selectedNumber}`,
          payload
        );

        if (response.status === 200) return true;
        else return false;
      } catch (error) {
        errorMessages.push({
          contact: contact.name || contact.phoneNumber,
          message: error.response.data,
        });
        return false;
      }
    });
    const results = await Promise.all(promises);

    const successCount = results.filter((res) => res === true).length;
    const failedCount = selectedContacts.length - successCount;
    if (successCount > 0) {
      showToast(
        `${successCount} ${t("Message sent successfully!")}`,
        `${t("Success")}`
      );
    }

    if (failedCount > 0) {
      showToast(
        `${failedCount} ${t("Failed to send message")}`,
        `${t("Error")}`
      );
      errorMessages.forEach((error) => {
        showToast(`${error.contact}: ${error.message}`, `${t("Error")}`);
      });
    }

    setMessage("");
    setSelectedNumber(null);
    setSelectedContacts([]);
  };

  const onPhoneNumberChange = (event) => {
    const selectedItem = event.itemData;
    setSelectedNumber(selectedItem ? selectedItem.phoneNumberId : null);
  };

  const onContactSelect = (contacts) => {
    setSelectedContacts(contacts);
    setIsModalOpen(false);
  };

  const openModal = () => {
    setIsModalOpen(true);
  };

  useEffect(() => {
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!data) {
    return <div></div>;
  }
  return (
    <div
      className="content e-content-animation"
      style={{ display: "flex", justifyContent: "center" }}
    >
      <div className="control-wrapper">
        <form id="send-message" onSubmit={onSubmit}>
          <ComboBoxComponent
            placeholder={t("Sender")}
            dataSource={data}
            fields={{ text: "phoneNumber", value: "phoneNumberId" }}
            value={selectedNumber}
            change={onPhoneNumberChange}
            required
            allowCustom={false}
          ></ComboBoxComponent>
          <ButtonComponent
            cssClass="e-css e-btn"
            type="button"
            onClick={openModal}
          >
            {t("To")}
          </ButtonComponent>
          <small>
            {selectedContacts.length <= 3
              ? selectedContacts
                  .map((contact) =>
                    contact.definition.split(" ").slice(0, 2).join(" ")
                  )
                  .join(", ")
              : `${selectedContacts
                  .slice(0, 3)
                  .map((contact) =>
                    contact.definition.split(" ").slice(0, 2).join(" ")
                  )
                  .join(", ")} ${t("and")} ${selectedContacts.length - 3} ${t(
                  "more"
                )}`}
          </small>
          <TextAreaComponent
            id="input-message"
            className="e-input"
            type="text"
            placeholder={t("Message")}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            required
            resizeMode="None"
            rows={3}
          />
          <ButtonComponent cssClass="e-primary e-outline" type="submit">
            {t("Send Message")}
          </ButtonComponent>
        </form>
        <ContactModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSelectContact={onContactSelect}
        />
      </div>
    </div>
  );
};

export default SendMessagePage;
